'use client';

import { useMemo, type ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';
import { createI18nInstance } from '@/src/lib/i18n-instance';
import { LanguageProvider } from '@/src/contexts/LanguageContext';
import type { Language } from '@/src/contexts/LanguageContext';
import type { Dict } from '@/src/i18n/deepMerge';

/**
 * Client-side provider tree shared by every localized route. The i18n
 * instance is built from the URL language and that language's translations,
 * which the server layout passes in. Both the server-rendered HTML (static
 * export) and the hydrated client therefore render from the same dictionary,
 * and the client never downloads the other five languages.
 */
export default function Providers({
  lang,
  translation,
  children,
}: {
  lang: string;
  translation: Dict;
  children: ReactNode;
}) {
  const i18n = useMemo(() => createI18nInstance(lang, translation), [lang, translation]);

  return (
    <I18nextProvider i18n={i18n}>
      <LanguageProvider initialLanguage={lang as Language}>
        {children}
      </LanguageProvider>
    </I18nextProvider>
  );
}
